import _ from 'lodash';
import { useMemo, useState } from 'react';

import TradeYourOwnRiskAlert from '@/components/Alert/TradeYourOwnRiskAlert';
import { TableCard } from '@/components/TableCard';
import { useTableColumns } from '@/components/TableCard/tableConfigs';
import TableSearchBar from '@/components/TableSearchBar';
import { PAIR_PAGE_TYPE, TABLE_TYPES } from '@/constants/global';
import {
  useFilteredTradingPairs,
  useMarginSearch,
  useMarketList,
  useWatchMarketListChange,
} from '@/features/market/hooks';
import { useChainId } from '@/hooks/web3/useChain';

import NoSearchResult from '@/assets/svg/search_empty.svg?react';
import { useMockDevTool } from '@/components/Mock';
import PairSearch from '@/components/TableSearchBar/PairSearch';
import { useBackendChainConfig } from '@/features/config/hook';
import { IMarketPair } from '@/features/market/types';
import { useIsEarnPage } from '@/hooks';
import { useSideNavigate } from '@/hooks/useRouterNavigate';
import { CUSTOM_SELECTOR } from '@/types/global';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import './index.less';
import { marketColumnRowKey } from './tableTitles';
interface IProps {
  onClose: () => void;
}
export default function TradingPairs({ onClose }: IProps): JSX.Element {
  const chainId = useChainId();
  const { t } = useTranslation();
  const isEarn = useIsEarnPage();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sorter, setSorter] = useState<any>();
  const chainConfig = useBackendChainConfig(chainId);
  const { pairPageNavigate } = useSideNavigate();
  const {
    marginSearchProps: { marginCustomSelectorId, search, marginSelector },
  } = useMarginSearch();
  const { marketPairList, isFetched } = useMarketList(chainId);

  const { filteredPairs, showMargin } = useFilteredTradingPairs();
  const columnDefs = useTableColumns(TABLE_TYPES.TRADING_PAIR, showMargin);

  const searchEmptyLine = useMemo(() => {
    return search ? t('common.market.noSearchResult') : undefined;
  }, [search, t]);

  //  only show pairs in current chain
  const filteredPairsByChainId = useMemo(
    () => filteredPairs?.filter((p) => p.chainId === chainId),
    [filteredPairs, chainId],
  );
  const sortedPairs = useMemo(
    () =>
      chainConfig?.trendingPairs?.length && !_.get(sorter, ['column'])
        ? _.orderBy(
            filteredPairsByChainId,
            [
              (p) => {
                const index = chainConfig.trendingPairs?.indexOf(p.symbol);
                return index === undefined || index < 0 ? filteredPairsByChainId?.length : index;
              },
            ],
            ['asc'],
          )
        : filteredPairsByChainId,
    [chainConfig?.trendingPairs, filteredPairsByChainId, sorter],
  );
  useWatchMarketListChange(chainId);
  const { isMockSkeleton } = useMockDevTool();

  return (
    <div className={classNames('market_table_card')}>
      {/* <TradingPairsHeader /> */}
      {/* <HidePairsBtn /> */}
      <div className="market_table_card-top">
        <PairSearch />
        <TableSearchBar />
      </div>
      {marginSelector === CUSTOM_SELECTOR.CUSTOM && marginCustomSelectorId && (
        <TradeYourOwnRiskAlert
          style={{ marginBottom: 4 }}
          i18nId={`common.tradeYourOwnRiskAlert.${marginCustomSelectorId}.list`}
        />
      )}
      <div className="market_table_card-table-container syn-scrollbar">
        <TableCard
          className="syn-trading-pairs-table"
          cardTitle=""
          sticky={{ offsetHeader: 0 }}
          loading={isMockSkeleton || (!isFetched && !marketPairList?.length)}
          loadingItem={isMockSkeleton || (!isFetched && !marketPairList?.length)}
          total={filteredPairsByChainId?.length}
          rowCount={10}
          rowClassName={classNames('market_table_card-row')}
          tableChange={(_e, _f, s) => {
            return setSorter(s);
          }}
          emptyDesc={searchEmptyLine}
          emptyIcon={search ? <NoSearchResult width={128} height={128} /> : undefined}
          emptyType="vertical"
          showEmptyTitle={!search}
          onRow={(record: IMarketPair) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).tagName === 'BUTTON') return;

              pairPageNavigate(
                record.symbol || '',
                isEarn ? PAIR_PAGE_TYPE.EARN : PAIR_PAGE_TYPE.TRADE,
                record.chainId,
              );
              onClose();
            },
          })}
          rowKey={(record: IMarketPair) => `${record?.symbol}-${marketColumnRowKey(record)}`}
          columns={columnDefs}
          dataSource={sortedPairs}
        />
      </div>
    </div>
  );
}
